import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE']);
const ATTRS = ['placeholder', 'title', 'aria-label'];
const HAS_WORD = /[A-Za-zÀ-ÿ]{2,}/;
const cache = new Map(); // lang -> Map(src -> dst)

// Whole-UI runtime translation: walks visible text nodes + placeholders, translates them via
// /api/i18n/translate (cached server-side) and keeps them in sync through a MutationObserver.
const AutoTranslate = () => {
  const { i18n } = useTranslation();
  const { user, getAuthHeader } = useAuth();
  const lang = (i18n.language || 'es').slice(0, 2);
  const tracked = useRef(new Map()); // node -> { orig, attr|null }
  const timer = useRef(null);
  const inflight = useRef(false);

  useEffect(() => {
    const root = document.body;

    const restore = () => {
      tracked.current.forEach((info, node) => {
        if (info.attr) { if (node.setAttribute) node.setAttribute(info.attr, info.orig); }
        else if (node.nodeValue !== info.orig) node.nodeValue = info.orig;
      });
      tracked.current.clear();
    };

    if (lang === 'es' || !user) { restore(); return undefined; }
    if (!cache.has(lang)) cache.set(lang, new Map());
    const dict = cache.get(lang);
    let cancelled = false; // set on cleanup so late responses never re-apply an old language

    const shouldSkip = (el) => {
      for (let e = el; e && e !== root; e = e.parentElement) {
        if (SKIP_TAGS.has(e.tagName) || e.getAttribute('translate') === 'no' || e.hasAttribute('data-no-translate')) return true;
      }
      return false;
    };

    const collect = () => {
      const items = []; // { node, attr, src }
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
        acceptNode: (n) => {
          if (n.nodeType === Node.ELEMENT_NODE) return SKIP_TAGS.has(n.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n = walker.nextNode();
      while (n) {
        if (n.nodeType === Node.TEXT_NODE) {
          const info = tracked.current.get(n);
          const raw = n.nodeValue;
          const src = info && info.dst === raw ? null : raw; // unchanged translated node → skip
          if (src && HAS_WORD.test(src) && !shouldSkip(n.parentElement)) items.push({ node: n, attr: null, src });
        } else {
          ATTRS.forEach((a) => {
            if (!n.hasAttribute(a) || shouldSkip(n)) return;
            const key = `${a}`;
            const info = tracked.current.get(n);
            const raw = n.getAttribute(a);
            if (info && info.attr === key && info.dst === raw) return;
            if (HAS_WORD.test(raw)) items.push({ node: n, attr: key, src: raw });
          });
        }
        n = walker.nextNode();
      }
      return items;
    };

    const apply = (items) => {
      if (cancelled) return;
      items.forEach(({ node, attr, src }) => {
        const trimmed = src.trim();
        const dst = dict.get(trimmed);
        if (!dst || dst === trimmed) return;
        const out = src.replace(trimmed, dst);
        if (attr) { node.setAttribute(attr, out); tracked.current.set(node, { orig: src, attr, dst: out }); }
        else { node.nodeValue = out; tracked.current.set(node, { orig: src, attr: null, dst: out }); }
      });
    };

    const run = async () => {
      if (cancelled) return;
      if (inflight.current) { schedule(); return; }
      const items = collect();
      if (!items.length) return;
      const missing = Array.from(new Set(items.map((i) => i.src.trim()).filter((s) => s && !dict.has(s))));
      apply(items);
      if (!missing.length) return;
      inflight.current = true;
      try {
        for (let i = 0; i < missing.length; i += 200) {
          const res = await axios.post(`${API}/i18n/translate`, { lang, texts: missing.slice(i, i + 200) }, { headers: { Authorization: getAuthHeader() } });
          Object.entries(res.data.translations || {}).forEach(([k, v]) => dict.set(k, v));
        }
        if (!cancelled) apply(collect());
      } catch (_) { /* leave original text */ } finally { inflight.current = false; }
    };

    const schedule = () => { clearTimeout(timer.current); timer.current = setTimeout(run, 250); };
    // body-level so portals (dialogs, toasts, selects) are covered too
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ATTRS });
    run();
    return () => { cancelled = true; observer.disconnect(); clearTimeout(timer.current); inflight.current = false; restore(); };
  }, [lang, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default AutoTranslate;
